import {
  addLanguage,
  deleteLanguage,
  updateLanguageTextValue,
} from '@ps/bridge/qc/scripts/language'
import * as gamePlayNode from '@ps/bridge/qc/node/game-play'
import store from '@ps/store'
import i18n from 'i18n'
import gameIframe from '@ps/bridge/iframe/iframe'
import * as editorScript from '@ps/bridge/qc/scripts/editor'
import { EditorDataScript } from '@ps/bridge/qc/scripts/editor'
import { switchLanguage } from '@ps/workbench/language'

const addLanguageExecute = ({
  newValue,
}: {
  newValue: {
    lang: string
    value: i18n.Language
  }
}) => {
  const { lang, value } = newValue
  addLanguage({ lang, value })
  gamePlayNode.addLanguage(lang)
  const langList = [...gamePlayNode.getLanguages()]
  store.commit('workspace/language/updateLanguageList', langList)
  store.dispatch('workspace/rightPane/switchToGlobalConfigTab')
}

const deleteLanguageExecute = ({
  newValue,
}: {
  newValue: {
    lang: string
  }
}) => {
  const { lang } = newValue
  deleteLanguage(lang)
  gamePlayNode.deleteLanguage(lang)
  const languageList = store.state.workspace.language.languageList
  store.commit(
    'workspace/language/updateLanguageList',
    languageList.filter((_lang: string) => {
      return _lang !== lang
    }),
  )
  store.dispatch('workspace/rightPane/switchToGlobalConfigTab')
}

export type AddLanguageActionValue = {
  newValue: {
    lang: string
    value: i18n.Language
  }
  oldValue: {
    lang: string
  }
}

export function getAddLanguageAction(value: AddLanguageActionValue) {
  return {
    execute: addLanguageExecute,
    undo: ({ oldValue }: AddLanguageActionValue) => {
      const { lang } = oldValue
      deleteLanguageExecute({ newValue: { lang } })
      store.dispatch('workspace/rightPane/switchToGlobalConfigTab')
    },
    value,
    methodName: 'getAddLanguageAction',
  }
}

export type DeleteLanguageActionValue = {
  newValue: {
    lang: string
  }
  oldValue: {
    lang: string
    value: i18n.Language
  }
}

export function getDeleteLanguageAction(value: DeleteLanguageActionValue) {
  return {
    execute: deleteLanguageExecute,
    undo: ({ oldValue }: DeleteLanguageActionValue) => {
      const { lang, value } = oldValue
      addLanguageExecute({
        newValue: {
          lang,
          value,
        },
      })
      store.dispatch('workspace/rightPane/switchToGlobalConfigTab')
    },
    value,
    methodName: 'getDeleteLanguageAction',
  }
}

export type UpdateLanguageActionValue = {
  newValue: {
    lang: string
    text: {
      [key: string]: string
    }
  }
  oldValue: {
    lang: string
    text: {
      [key: string]: string
    }
  }
}

export function getUpdateLanguageAction(value: UpdateLanguageActionValue) {
  const isCurrentLang = (lang: string) =>
    store.state.workspace.language.currentLanguage === lang
  return {
    execute: ({ newValue }: UpdateLanguageActionValue) => {
      const { text, lang } = newValue
      updateLanguageTextValue({
        lang,
        text,
      })
      // 如果修改的是当前语言,则需要联动更新
      if (isCurrentLang(lang)) {
        store.dispatch('workspace/language/switchLanguage', {
          lang,
          record: false,
        })
      }
    },
    undo: ({ oldValue }: UpdateLanguageActionValue) => {
      const { text, lang } = oldValue
      updateLanguageTextValue({
        lang,
        text,
      })
      // 如果修改的是当前语言,则需要联动更新
      if (isCurrentLang(lang)) {
        store.dispatch('workspace/language/switchLanguage', {
          lang,
          record: false,
        })
      }
    },
    value,
    methodName: 'getUpdateLanguageAction',
  }
}

export type SwitchLanguageActionValue = {
  newValue: {
    lang: string
  }
  oldValue: {
    lang: string
  }
}

export function getSwitchLanguageAction(value: SwitchLanguageActionValue) {
  const update = (lang: string) => {
    if (!lang) {
      console.log('切换语言为空')
      return
    }
    switchLanguage(lang)
    store.dispatch('workspace/language/updateCurrentLanguage', { lang })
  }
  return {
    execute: ({ newValue }: SwitchLanguageActionValue) => {
      const { lang } = newValue
      update(lang)
    },
    undo: ({ oldValue }: SwitchLanguageActionValue) => {
      const { lang } = oldValue
      update(lang)
    },
    value,
    methodName: 'getSwitchLanguageAction',
  }
}

export type UpdateDefaultLanguageActionValue = {
  newValue: {
    lang: string
  }
  oldValue: {
    lang: string
  }
}

export function getUpdateDefaultLanguageAction(
  value: UpdateDefaultLanguageActionValue,
) {
  const update = ({ lang }: { lang: string }) => {
    const gamePlay = gameIframe.gamePlay
    const ped = editorScript.getScript(gamePlay) as EditorDataScript
    ped.languageConfig.defaultLang = lang
    store.state.workspace.language.defaultLanguage = lang
  }
  return {
    execute: ({ newValue }: UpdateDefaultLanguageActionValue) => {
      update(newValue)
    },
    undo: ({ oldValue }: UpdateDefaultLanguageActionValue) => {
      update(oldValue)
    },
    value,
    methodName: 'getUpdateDefaultLanguageAction',
  }
}
